import { useEffect, useRef, useState, type ReactNode } from "react";
import { animate, motion, useMotionValue, useMotionValueEvent, useReducedMotion, useSpring } from "framer-motion";

/**
 * The homepage opens on a full-bleed stage: a dark field of log rows with gaps
 * (the events that were never collected) and the Huntbase mark roaming it as a
 * lens. Wherever the lens passes, the rows sharpen and the gaps fill in violet.
 * The mark draws itself in, sweeps the stage once, then rests on the right and
 * follows the pointer anywhere in the hero. Nothing loops.
 *
 * Children (the headline block) render above the field.
 */

const W = 1600;
const H = 900;
const MARK =
  "m 129.55747,51.981323 a 79.709267,79.709267 0 0 0 -79.709351,79.709347 79.709267,79.709267 0 0 0 79.709351,79.70934 79.709267,79.709267 0 0 0 50.54058,-18.07279 l 31.63424,31.63424 0.0832,-0.0832 a 9.2604122,9.2604122 0 0 0 6.46576,2.6634 9.2604122,9.2604122 0 0 0 9.26041,-9.26041 9.2604122,9.2604122 0 0 0 -2.64841,-6.48074 l 0.0682,-0.0682 -31.89728,-31.89727 A 79.709267,79.709267 0 0 0 209.2668,131.69069 79.709267,79.709267 0 0 0 129.55746,51.981343 Z m 0,12.753743 a 66.95578,66.95578 0 0 1 66.9556,66.955604 66.95578,66.95578 0 0 1 -66.9556,66.9556 66.95578,66.95578 0 0 1 -66.955607,-66.9556 66.95578,66.95578 0 0 1 66.955607,-66.955604 z M 92.152411,181.29541 V 82.085608 H 110.85504 V 121.76953 H 148.2603 V 82.085608 h 18.70262 V 181.29541 H 148.2603 v -39.68392 h -37.40526 v 39.68392 z";
const MARK_CX = 129.55747;
const MARK_CY = 131.69069;
const MARK_SCALE = 1.5;
const LENS_R = 66.95578 * MARK_SCALE;
/** Where the mark rests: right of the headline on wide screens, top-right décor on narrow ones. */
const REST_WIDE = { x: 1130, y: 410 };
const REST_NARROW = { x: 990, y: 80 };
const isNarrow = () => typeof window !== "undefined" && window.matchMedia("(max-width: 1023px)").matches;

/** Deterministic field so the stage is identical on every load. */
function field() {
  let seed = 7;
  const rnd = () => {
    seed = (seed * 16807) % 2147483647;
    return seed / 2147483647;
  };
  const rows: Array<{ y: number; segs: Array<{ x: number; w: number; hidden: boolean }> }> = [];
  for (let y = 48; y < H; y += 40) {
    let x = 24 + rnd() * 60;
    const segs = [];
    while (x < W - 40) {
      const w = 60 + rnd() * 180;
      segs.push({ x, w: Math.min(w, W - 24 - x), hidden: rnd() < 0.24 });
      x += w + 24 + rnd() * 70;
    }
    rows.push({ y, segs });
  }
  return rows;
}
const ROWS = field();

const LABELS: Array<{ x: number; y: number; text: string }> = [
  { x: 300, y: 250, text: "process_activity · WKS-2231 · never shipped" },
  { x: 900, y: 410, text: "authentication · m.reyes · stored" },
  { x: 560, y: 690, text: "file_activity · upd.dll · live" },
  { x: 1220, y: 170, text: "network_activity · 203.0.113.9 · live" },
];

export function HeroStage({
  children,
  className,
  avoid,
}: {
  children: ReactNode;
  className?: string;
  /** The copy block. The lens never crosses it, so the mark never sits under the type. */
  avoid?: React.RefObject<HTMLElement | null>;
}) {
  const reduce = useReducedMotion();
  const wrap = useRef<HTMLDivElement>(null);
  const [drawn, setDrawn] = useState(!!reduce);
  const narrow = useRef(isNarrow());
  const REST = narrow.current ? REST_NARROW : REST_WIDE;

  const tx = useMotionValue(reduce || narrow.current ? REST.x : W * 0.62);
  const ty = useMotionValue(reduce || narrow.current ? REST.y : H * 0.5);
  const cx = useSpring(tx, { stiffness: 50, damping: 18, mass: 1 });
  const cy = useSpring(ty, { stiffness: 50, damping: 18, mass: 1 });
  const sweeping = useRef(false);

  // The readout: how many never-logged rows sit under the lens right now.
  const [found, setFound] = useState(0);
  const count = () => {
    const x = cx.get();
    const y = cy.get();
    let n = 0;
    for (const r of ROWS) {
      if (Math.abs(r.y - y) > LENS_R) continue;
      for (const seg of r.segs) {
        if (!seg.hidden) continue;
        const mx = seg.x + seg.w / 2;
        if ((mx - x) ** 2 + (r.y - y) ** 2 <= LENS_R * LENS_R) n += 1;
      }
    }
    setFound(n);
  };
  const readoutText = `${found} ${found === 1 ? "event" : "events"} never logged`;
  // 13px mono is ~8px per character; the pill is sized to the text so it never overflows.
  const readoutWidth = 44 + readoutText.length * 8.2;
  useMotionValueEvent(cx, "change", count);
  useMotionValueEvent(cy, "change", count);
  useEffect(count, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (reduce || !drawn || narrow.current) return;
    sweeping.current = true;
    const path = [
      { x: 1350, y: 200 },
      { x: 1000, y: 720 },
      { x: 1420, y: 620 },
      REST,
    ];
    const ax = animate(tx, path.map((p) => p.x), { duration: 8, ease: "easeInOut" });
    const ay = animate(ty, path.map((p) => p.y), { duration: 8, ease: "easeInOut" });
    ax.then(() => {
      sweeping.current = false;
    });
    return () => {
      ax.stop();
      ay.stop();
    };
  }, [drawn, reduce, tx, ty, REST]);

  // Pointer → viewBox units, accounting for `slice` scaling of the background,
  // then clamped so the lens stays clear of the copy block.
  const onMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (reduce || !wrap.current || e.pointerType === "touch") return;
    if (sweeping.current) {
      sweeping.current = false;
      tx.stop();
      ty.stop();
    }
    const r = wrap.current.getBoundingClientRect();
    const s = Math.max(r.width / W, r.height / H);
    const ox = (r.width - W * s) / 2;
    const oy = (r.height - H * s) / 2;
    let x = (e.clientX - r.left - ox) / s;
    const y = (e.clientY - r.top - oy) / s;
    const block = avoid?.current?.getBoundingClientRect();
    if (block) {
      const blockRight = (block.right - r.left - ox) / s;
      const blockBottom = (block.bottom - r.top - oy) / s;
      if (y < blockBottom + LENS_R * 0.6) x = Math.max(x, blockRight + LENS_R * 1.05);
    }
    tx.set(Math.min(x, W - LENS_R * 0.4));
    ty.set(Math.max(LENS_R * 0.3, Math.min(y, H - LENS_R * 0.3)));
  };
  const onLeave = () => {
    if (reduce) return;
    tx.set(REST.x);
    ty.set(REST.y);
  };

  return (
    <div ref={wrap} className={className} onPointerMove={onMove} onPointerLeave={onLeave}>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="xMidYMid slice"
        className="absolute inset-0 h-full w-full"
        aria-hidden
      >
        <defs>
          <clipPath id="stage-lens">
            <motion.circle cx={cx} cy={cy} r={LENS_R} />
          </clipPath>
        </defs>

        {/* The field: what was collected. */}
        <g fill="#ffffff" fillOpacity={0.05}>
          {ROWS.map((r) =>
            r.segs
              .filter((s) => !s.hidden)
              .map((s) => <rect key={`${r.y}-${s.x}`} x={s.x} y={r.y - 5} width={s.w} height={10} rx={5} />)
          )}
        </g>

        {/* Under the lens: the rows sharpen, the gaps fill in, and a few rows get names. */}
        <g clipPath="url(#stage-lens)">
          <motion.circle cx={cx} cy={cy} r={LENS_R} fill="var(--primary-900)" fillOpacity={0.55} />
          {ROWS.map((r) =>
            r.segs.map((s) => (
              <rect
                key={`u-${r.y}-${s.x}`}
                x={s.x}
                y={r.y - 5}
                width={s.w}
                height={10}
                rx={5}
                fill={s.hidden ? "#ffffff" : "var(--indigo-300)"}
                fillOpacity={s.hidden ? 0.95 : 0.35}
              />
            ))
          )}
          {LABELS.map((l) => (
            <text
              key={l.text}
              x={l.x}
              y={l.y}
              fontFamily="var(--font-mono)"
              fontSize={13}
              letterSpacing="0.04em"
              fill="var(--indigo-300)"
            >
              {l.text}
            </text>
          ))}
        </g>

        {/* The mark, drawn in once. */}
        {/* On narrow screens the mark is décor behind the copy, so it steps back. */}
        <motion.g style={{ x: cx, y: cy, opacity: narrow.current ? 0.22 : 1 }}>
          <g transform={`scale(${MARK_SCALE}) translate(${-MARK_CX} ${-MARK_CY})`}>
            <motion.path
              d={MARK}
              stroke="#ffffff"
              strokeOpacity={0.55}
              strokeWidth={1.5 / MARK_SCALE}
              strokeLinejoin="round"
              fill="var(--primary-500)"
              initial={reduce ? { pathLength: 1, fillOpacity: 1 } : { pathLength: 0, fillOpacity: 0 }}
              animate={{ pathLength: 1, fillOpacity: 1 }}
              transition={
                reduce
                  ? { duration: 0 }
                  : { pathLength: { duration: 1.6, ease: "easeInOut" }, fillOpacity: { delay: 1.2, duration: 0.6 } }
              }
              onAnimationComplete={() => setDrawn(true)}
            />
          </g>
          {/* Live readout, riding with the lens: what it is finding right now. */}
          <motion.g
            initial={{ opacity: 0 }}
            animate={{ opacity: drawn ? 1 : 0 }}
            transition={{ duration: 0.4 }}
            style={{ x: -LENS_R * 1.12 - readoutWidth, y: -LENS_R * 0.92 }}
          >
            <rect x={0} y={-18} width={readoutWidth} height={36} rx={18} fill="var(--ink)" stroke="#ffffff" strokeOpacity={0.25} />
            <circle cx={18} cy={0} r={4} fill={found > 0 ? "var(--signal)" : "#ffffff"} fillOpacity={found > 0 ? 1 : 0.35} />
            <text x={32} y={5} fontFamily="var(--font-mono)" fontSize={13} letterSpacing="0.04em" fill="#ffffff">
              <motion.tspan
                key={found}
                initial={{ opacity: 0.3 }}
                animate={{ opacity: 1 }}
                fill={found > 0 ? "var(--indigo-300)" : "#ffffff"}
                fontWeight={600}
              >
                {found}
              </motion.tspan>
              <tspan fillOpacity={0.85}>{readoutText.slice(String(found).length)}</tspan>
            </text>
          </motion.g>
        </motion.g>
      </svg>
      {children}
    </div>
  );
}
